import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import { en } from "./en";
import { ru } from "./ru";

export type Lang = "en" | "ru";
export type Translate = (
  key: string,
  params?: Record<string, string | number>,
  fallback?: string,
) => string;

const DICTS: Record<Lang, Record<string, string>> = { en, ru };
const STORAGE_KEY = "qa.lang";

function detectLang(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "en" || saved === "ru") return saved;
  } catch {
    // Storage can be unavailable; the browser language decides instead.
  }
  return navigator.language?.toLowerCase().startsWith("ru") ? "ru" : "en";
}

function makeT(lang: Lang): Translate {
  const dict = DICTS[lang];
  return (key, params, fallback) => {
    let text = dict[key] ?? en[key as keyof typeof en] ?? fallback ?? key;
    if (params) {
      for (const [name, value] of Object.entries(params)) {
        text = text.replaceAll(`{${name}}`, String(value));
      }
    }
    return text;
  };
}

interface I18n {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: Translate;
}

// Pages render English when used without a provider (the unit tests do exactly
// that); the app itself always mounts the provider.
const defaultI18n: I18n = { lang: "en", setLang: () => {}, t: makeT("en") };

const I18nContext = createContext<I18n>(defaultI18n);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(detectLang);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo<I18n>(
    () => ({
      lang,
      setLang: (next) => {
        setLangState(next);
        try {
          localStorage.setItem(STORAGE_KEY, next);
        } catch {
          // Private mode: the choice just does not survive a reload.
        }
      },
      t: makeT(lang),
    }),
    [lang],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  return useContext(I18nContext);
}

/**
 * The EN/RU pill. It rides in the topbar with the rest of the chrome; the
 * sign-in screen has no topbar, so there it floats in the corner instead.
 */
export function LanguageSwitcher({ floating = false }: { floating?: boolean } = {}) {
  const { lang, setLang, t } = useI18n();
  return (
    <div
      className={floating ? "lang-switch floating" : "lang-switch"}
      role="group"
      aria-label={t("lang.label")}
    >
      {(["en", "ru"] as Lang[]).map((code) => (
        <button
          key={code}
          type="button"
          className={lang === code ? "on" : ""}
          aria-pressed={lang === code}
          onClick={() => setLang(code)}
        >
          {code.toUpperCase()}
        </button>
      ))}
    </div>
  );
}
